import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import type { PremiumReport } from "../../shared/types";
import { ReportView } from "../components/report/ReportView";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Section } from "../components/ui/Section";
import { getDevPremiumReport, getPremiumReport, getPremiumReportPdf } from "../lib/testApi";

function isLocalDevHost() {
  if (typeof window === "undefined") return false;
  return ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
}

function isDemoReportRoute() {
  if (typeof window === "undefined") return false;
  return window.location.pathname.startsWith("/report/demo/");
}

function safePdfFilename(reportId: string) {
  const safeReportId = reportId.replace(/[^a-zA-Z0-9_-]/g, "-").slice(0, 80) || "report";
  return `TryBrainTest-Report-${safeReportId}.pdf`;
}

export function ReportPage() {
  const { attemptId, reportId } = useParams();
  const [searchParams] = useSearchParams();
  const [report, setReport] = useState<PremiumReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const isDemoRoute = isDemoReportRoute();
  const canUseDevReport = !isDemoRoute || isLocalDevHost();
  const reportToken = searchParams.get("token");

  useEffect(() => {
    if (!canUseDevReport) return;
    setError(null);
    setReport(null);

    if (!attemptId) {
      if (reportId && reportToken) {
        getPremiumReport(reportId, reportToken)
          .then(setReport)
          .catch((err) => setError(err instanceof Error ? err.message : "Δεν μπορέσαμε να φορτώσουμε το report."));
      }
      return;
    }
    getDevPremiumReport(attemptId)
      .then(setReport)
      .catch((err) => setError(err instanceof Error ? err.message : "Δεν μπορέσαμε να φορτώσουμε το report."));
  }, [attemptId, canUseDevReport, reportId, reportToken]);

  if (!canUseDevReport) {
    return (
      <Section className="py-12 sm:py-16">
        <Card className="mx-auto max-w-2xl text-center">
          <h1 className="text-3xl font-bold text-white">404</h1>
          <p className="mt-3 text-slate-300">Η σελίδα δεν βρέθηκε.</p>
        </Card>
      </Section>
    );
  }

  if (!attemptId && (!reportId || !reportToken)) {
    return (
      <Section className="py-12 sm:py-16">
        <Card className="mx-auto max-w-2xl text-center">
          <h1 className="text-3xl font-bold text-white">TryBrainTest Premium Report</h1>
          <p className="mt-3 text-slate-300">Το report {reportId} θα είναι διαθέσιμο μετά το paid unlock.</p>
        </Card>
      </Section>
    );
  }

  if (error) {
    return (
      <Section className="py-12 sm:py-16">
        <Card className="mx-auto max-w-2xl text-center">
          <h1 className="text-3xl font-bold text-white">Δεν βρέθηκε report preview</h1>
          <p className="mt-3 text-slate-300">{error}</p>
          <Button href="/test" className="mt-7">Νέο test</Button>
        </Card>
      </Section>
    );
  }

  if (!report) {
    return (
      <Section className="py-12 sm:py-16">
        <Card className="mx-auto max-w-2xl text-center">
          <p className="text-slate-300">Φορτώνουμε το TryBrainTest Premium Report...</p>
        </Card>
      </Section>
    );
  }

  const handleDownloadPdf =
    reportId && reportToken
      ? async () => {
          setPdfLoading(true);
          setPdfError(null);
          try {
            const pdf = await getPremiumReportPdf(reportId, reportToken);
            const url = URL.createObjectURL(pdf);
            const link = document.createElement("a");
            link.href = url;
            link.download = safePdfFilename(report.reportId || reportId);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.setTimeout(() => URL.revokeObjectURL(url), 1000);
          } catch {
            setPdfError("Could not generate the PDF right now. Please try again in a moment.");
          } finally {
            setPdfLoading(false);
          }
        }
      : undefined;

  return <ReportView report={report} onDownloadPdf={handleDownloadPdf} pdfLoading={pdfLoading} pdfError={pdfError} />;
}
